import { useLayoutEffect, useRef } from 'react';

/**
 * Sfera armillare inline, usata come punto della "i" di "URANIA".
 * Nessun filtro glow/drop-shadow: richiesta esplicita dell'handoff
 * "Copertina (le tre anime)" — l'alone la rendeva lattiginosa e in
 * conflitto con le stelle vere dietro al masthead.
 */
function SferaArmillare() {
  return (
    <svg
      className="uo-mast__sphere-svg"
      viewBox="0 0 34 34"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="17" cy="17" r="15" fill="none" stroke="#A9D6FF" strokeWidth="0.9" opacity="0.85" />
      <ellipse
        cx="17"
        cy="17"
        rx="15"
        ry="6"
        fill="none"
        stroke="#A9D6FF"
        strokeWidth="0.9"
        opacity="0.85"
        transform="rotate(18 17 17)"
      />
      <ellipse
        cx="17"
        cy="17"
        rx="15"
        ry="6"
        fill="none"
        stroke="#A9D6FF"
        strokeWidth="0.9"
        opacity="0.85"
        transform="rotate(-42 17 17)"
      />
      <circle cx="17" cy="17" r="11" fill="rgba(20,34,68,.42)" />
      <circle
        cx="17"
        cy="17"
        r="15"
        fill="none"
        stroke="#A9D6FF"
        strokeWidth="0.6"
        strokeDasharray="1.4 4.8"
        opacity="0.65"
      />
      <circle cx="17" cy="17" r="1.8" fill="#EAF5FF" />
    </svg>
  );
}

const WORD = 'URANIA';
// Indice della "I" che porta la sfera: U-0 R-1 A-2 N-3 [I-4] A-5.
const I_INDEX = 4;
const SPHERE_EM = 0.56;
const GAP_ABOVE_CAP_PX = 14;

/**
 * Wordmark "URANIA" con la sfera armillare centrata sull'inchiostro della
 * "I" e sollevata di 14px sopra la cap-height — non un offset fisso, ma
 * calcolato dalle metriche reali del font via Canvas 2D (TextMetrics),
 * perché cambia con font-size responsive, fallback font e zoom del
 * browser. Ricalcolo su mount, `document.fonts.ready` e resize.
 */
export function UraniaMasthead() {
  const wrapRef = useRef<HTMLDivElement>(null);
  const wordRef = useRef<HTMLHeadingElement>(null);
  const sphereRef = useRef<HTMLSpanElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useLayoutEffect(() => {
    function reposition() {
      const wrapEl = wrapRef.current;
      const wordEl = wordRef.current;
      const sphereEl = sphereRef.current;
      if (!wrapEl || !wordEl || !sphereEl) return;

      const cs = getComputedStyle(wordEl);
      const fontSizePx = parseFloat(cs.fontSize);
      if (!fontSizePx) return;

      if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;

      ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      if ('letterSpacing' in ctx) {
        try {
          (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = cs.letterSpacing;
        } catch {
          // Browser espone la proprietà ma non accetta il valore: ignora,
          // la stima resta comunque ragionevole senza tracking applicato.
        }
      }

      const before = WORD.slice(0, I_INDEX);
      const upToI = WORD.slice(0, I_INDEX + 1);

      const mBefore = ctx.measureText(before);
      const mUpToI = ctx.measureText(upToI);
      const mI = ctx.measureText('I');
      const mWord = ctx.measureText(WORD);

      const iCenterX = (mBefore.width + mUpToI.width) / 2;
      const capHeight = mI.actualBoundingBoxAscent;
      const lineTopToBaseline = mWord.fontBoundingBoxAscent;

      const sphereSize = fontSizePx * SPHERE_EM;

      const wrapRect = wrapEl.getBoundingClientRect();
      const wordRect = wordEl.getBoundingClientRect();
      const wordTopWithinWrap = wordRect.top - wrapRect.top;
      const wordLeftWithinWrap = wordRect.left - wrapRect.left;

      const capTopY = wordTopWithinWrap + lineTopToBaseline - capHeight;
      const sphereTopY = capTopY - GAP_ABOVE_CAP_PX - sphereSize;
      const sphereLeftX = wordLeftWithinWrap + iCenterX - sphereSize / 2;

      sphereEl.style.top = `${sphereTopY}px`;
      sphereEl.style.left = `${sphereLeftX}px`;
      sphereEl.style.width = `${sphereSize}px`;
      sphereEl.style.height = `${sphereSize}px`;
    }

    reposition();
    document.fonts.ready.then(reposition).catch(() => {});

    const ro = new ResizeObserver(reposition);
    if (wordRef.current) ro.observe(wordRef.current);
    window.addEventListener('resize', reposition);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', reposition);
    };
  }, []);

  return (
    <div className="uo-mast" ref={wrapRef}>
      <h1 className="uo-mast__word" ref={wordRef} aria-label="URANIA">
        <span className="uo-mast__uran" aria-hidden="true">
          URAN
        </span>
        <span className="uo-mast__ia" aria-hidden="true">
          IA
        </span>
      </h1>
      <span className="uo-mast__sphere" ref={sphereRef}>
        <SferaArmillare />
      </span>
    </div>
  );
}
